import { createSlice } from '@reduxjs/toolkit';
import apiV3 from '../services/apiV3';
import api from '../services/index';

export const quoteSlice = createSlice({
  name: 'quote',
  initialState: {
    formDetails: {},
    formPanels: [],
    formPanelWidgets: [],
    formConfigs: {},
    quoteDetails: {},
    solarOptions: [],
    selectedSolarOption: {},
  },
  reducers: {
    setFormDetails: (state, { payload }) => {
      state.formDetails = payload;
    },
    setFormPanels: (state, { payload }) => {
      state.formPanels = payload;
    },
    setFormPanelWidgets: (state, { payload }) => {
      state.formPanelWidgets = payload;
    },
    setFormConfigs: (state, { payload }) => {
      state.formConfigs = payload;
    },
    setQuoteDetails: (state, { payload }) => {
      state.quoteDetails = payload;
    },
    setSolarOptions: (state, { payload }) => {
      state.solarOptions = payload;
    },
    setSelectedSolarOption: (state, { payload }) => {
      state.selectedSolarOption = payload;
    },
  },
});

const getHeaders = api_key => {
  return { headers: { 'x-api-key': api_key } };
};

const getTokenHeader = token => {
  return { headers: { Authorization: `Bearer ${token}` } };
};

export const getFormDetails = payload => async dispatch => {
  try {
    const { form_id, store = false } = payload;
    const { data } = await api.get(`/self-service/forms/${form_id}`);
    if (store) dispatch(setFormDetails(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getFormPanels =
  ({ api_key, form_id, store = false }) =>
    async dispatch => {
      try {
        const { data } = await api.get(`/self-service/forms/${form_id}/panels`, getHeaders(api_key));
        if (store) dispatch(setFormPanels(data));
        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    };

export const getFormPanelWidgets =
  ({ api_key, panel_id, store }) =>
    async dispatch => {
      try {
        const { data } = await api.get(`/self-service/panels/${panel_id}/widgets`, getHeaders(api_key));
        if (store) dispatch(setFormPanelWidgets(data));
        return Promise.resolve(data);
      } catch (error) {
        return Promise.reject(error);
      }
    };



export const getFormConfigs = payload => async dispatch => {
  try {
    const { form_id, api_key, store } = payload;
    const { data } = await api.get(`/forms/${form_id}/configs`, getHeaders(api_key));
    if (store) dispatch(setFormConfigs(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

// Removed getJobTypes - no longer needed for embedded forms widget

export const getQuoteDetails = payload => async dispatch => {
  try {
    const { quote_id, token, store } = payload;
    const { data } = await api.get(`/self-service/quotes/${quote_id}`, getTokenHeader(token));
    if (store) dispatch(setQuoteDetails(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getPropertySolarDetails = payload => async dispatch => {
  try {
    const { property_id, api_key } = payload;
    const { data } = await api.get(`/properties/${property_id}/solar_options`, getHeaders(api_key));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getProductComponents = payload => async () => {
  try {
    const { product_id, api_key, params = {} } = payload;
    const { data } = await apiV3.get(`/api/products/${product_id}/components`, { ...getHeaders(api_key), params });
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateQuote = payload => async dispatch => {
  try {
    const { quote_id, token, request } = payload;
    const { data } = await api.put(`/self-service/quotes/${quote_id}`, request, { ...getTokenHeader(token) });
    dispatch(setQuoteDetails(data));
    return Promise.resolve(data);
  } catch (error) {
    return Promise.reject(error);
  }
};

// ============================================
// SESSION API FUNCTIONS
// ============================================
// TODO: Update these endpoints based on Mobile App repository findings
// Search Mobile App for: "/self-service/sessions" or "/sessions"

/**
 * Fetch session submission data to pre-fill form
 * @param {Object} payload - { session_id, api_key }
 * @returns {Promise} Transformed session data with values keyed by lexicon
 *
 * API endpoint:
 * GET /self-service/form_sessions/{session_id}/submission_data
 * Headers: { 'x-api-key': api_key }
 *
 * Response structure:
 * [
 *   {
 *     id: "...",
 *     session: { id: "..." },
 *     panel_widget_component: { id: "...", name: "..." },
 *     submitted_data: "...",
 *     lexicon: "primary_contact.name",
 *     note: null,
 *     image: null,
 *     component_media: []
 *   },
 *   ...
 * ]
 */
/**
 * Get session details (metadata including session_end status)
 * @param {Object} payload - { session_id, api_key }
 * @returns {Promise} Session details object
 */
export const getSessionDetails = payload => async dispatch => {
  try {
    const { session_id, api_key } = payload;
    const url = `/form_sessions/${session_id}`;
    const headers = getHeaders(api_key);

    const { data } = await api.get(url, headers);

    return Promise.resolve({
      session_id,
      ...data,
    });
  } catch (error) {
    console.error('Error fetching session details:', error);
    return Promise.reject(error);
  }
};

export const getSession = payload => async dispatch => {
  try {
    const { session_id, api_key } = payload;
    // Use /form_sessions/ (not /self-service/form_sessions/) based on API structure
    const url = `/form_sessions/${session_id}/submission_data`;
    const headers = getHeaders(api_key);

    const { data } = await api.get(url, headers);

    // Transform array response into values object keyed by lexicon
    const values = {};

    if (Array.isArray(data)) {
      data.forEach(item => {
        const lexicon = item.lexicon;
        const submittedData = item.submitted_data;

        if (lexicon) {
          // For signatures, prefer image field if available, otherwise use submitted_data
          // Store signature using its lexicon key (e.g., "contact.signature")
          if (item.image) {
            values[lexicon] = item.image; // Image data (base64 or URL)
          } else if (submittedData) {
            values[lexicon] = submittedData;
          }
        }
      });
    }

    // Return transformed data structure
    return Promise.resolve({
      session_id,
      values,
      raw_data: data, // Keep raw data for debugging
    });
  } catch (error) {
    console.error('Error fetching session:', error);
    return Promise.reject(error);
  }
};

/**
 * Update session submission data (save form progress)
 * @param {Object} payload - { session_id, api_key, values, components }
 * @param {Object} payload.values - Form values object keyed by lexicon
 * @param {Array} payload.components - Array of component objects with id and lexicon
 * @returns {Promise} Updated session data
 *
 * API endpoint:
 * PUT /self-service/form_sessions/{session_id}/submission_data
 * Headers: { 'x-api-key': api_key }
 * Body: [
 *   {
 *     panel_widget_component: { id: "..." },
 *     submitted_data: "...",
 *     lexicon: "..."
 *   },
 *   ...
 * ]
 */
export const updateSession = payload => async dispatch => {
  try {
    const { session_id, api_key, values, components } = payload;

    // Create a map of lexicon to component id from components array
    const lexiconToComponentId = {};
    if (Array.isArray(components)) {
      components.forEach(row => {
        if (Array.isArray(row)) {
          row.forEach(component => {
            if (component.lexicon) {
              lexiconToComponentId[component.lexicon] = component.id;
            }
            // Handle multichoice fallback lexicon
            if (component.component_type === 'MULTICHOICE' && !component.lexicon) {
              const fallbackLexicon = `multichoice_${component.id}`;
              lexiconToComponentId[fallbackLexicon] = component.id;
            }
          });
        }
      });
    }

    // Transform values object into array format required by API
    const submissionDataArray = Object.keys(values)
      .filter(lexicon => {
        // Only include if we have a component ID for this lexicon
        // Allow empty strings/null for signatures (to clear them) - check if it's a signature field
        const isSignature = lexicon.toLowerCase().includes('signature');
        const hasValue = values[lexicon] !== null && values[lexicon] !== undefined && values[lexicon] !== '';
        // Include if: has value OR is signature (to allow clearing signatures)
        // Note: API requires submitted_data to not be null, so signatures only use image field
        return lexiconToComponentId[lexicon] && (hasValue || isSignature);
      })
      .map(lexicon => {
        const value = values[lexicon];
        const isSignature = lexicon.toLowerCase().includes('signature');

        // For signatures, use image field with base64 string
        // For other fields, use submitted_data
        const payload = {
          panel_widget_component: {
            id: lexiconToComponentId[lexicon],
          },
          lexicon: lexicon,
        };

        if (isSignature) {
          // Signature: extract base64 from data URL if needed, or use as-is
          // If empty/null, set image to null to clear it
          if (!value || value === '') {
            payload.image = null;
          } else {
            let base64String = value;
            if (typeof value === 'string' && value.startsWith('data:image/')) {
              const base64Match = value.match(/^data:image\/[^;]+;base64,(.+)$/);
              if (base64Match) {
                base64String = base64Match[1];
              }
            }
            payload.image = base64String;
          }
          // API requires submitted_data to not be null - for signatures, set to empty string
          payload.submitted_data = '';
        } else {
          // For other fields, set submitted_data (must not be null per API validation)
          // If empty string, keep as empty string (don't convert to null)
          payload.submitted_data = value === '' ? '' : value;
        }

        return payload;
      });


    const { data } = await api.put(
      `/form_sessions/${session_id}/submission_data`,
      submissionDataArray,
      getHeaders(api_key)
    );
    return Promise.resolve(data);
  } catch (error) {
    console.error('Error updating session:', error);
    return Promise.reject(error);
  }
};

/**
 * Close/complete session
 * @param {Object} payload - { session_id, api_key }
 * @returns {Promise} Closed session data
 *
 * API endpoint:
 * PUT /form_sessions/{session_id}
 * Headers: { 'x-api-key': api_key }
 * Body: { "session_end": timestamp } (Unix timestamp in seconds as string)
 */
export const closeSession = payload => async dispatch => {
  try {
    const { session_id, api_key } = payload;
    // Generate timestamp (Unix timestamp in seconds)
    const session_end = Math.floor(Date.now() / 1000).toString();


    // PUT to /form_sessions/{session_id} with session_end timestamp
    const { data } = await api.put(
      `/form_sessions/${session_id}`,
      { session_end },
      getHeaders(api_key)
    );
    return Promise.resolve(data);
  } catch (error) {
    console.error('Error closing session:', error);
    return Promise.reject(error);
  }
};

/**
 * Reopen a closed session (set session_end to null)
 * @param {Object} payload - { session_id, api_key }
 * @returns {Promise} Updated session data
 *
 * API endpoint:
 * PUT /form_sessions/{session_id}
 * Headers: { 'x-api-key': api_key }
 * Body: { "session_end": null }
 */
export const reopenSession = payload => async dispatch => {
  try {
    const { session_id, api_key } = payload;


    // PUT to /form_sessions/{session_id} with session_end set to null
    const { data } = await api.put(
      `/form_sessions/${session_id}`,
      { session_end: null },
      getHeaders(api_key)
    );
    return Promise.resolve(data);
  } catch (error) {
    console.error('Error reopening session:', error);
    return Promise.reject(error);
  }
};

export const {
  setFormDetails,
  setFormPanels,
  setFormPanelWidgets,
  setFormConfigs,
  setQuoteDetails,
  setSolarOptions,
  setSelectedSolarOption,
} = quoteSlice.actions;
export default quoteSlice.reducer;
